import React, { useContext, useEffect, useRef, useState } from 'react'

import cx from 'classnames'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { Icon } from '@driftt/dds'
import { faArrowPointer } from '@driftt/dds.icons/light/faArrowPointer'
import { faCalendarCheck } from '@driftt/dds.icons/light/faCalendarCheck'
import { faCameraWeb } from '@driftt/dds.icons/light/faCameraWeb'
import { faCirclePlay } from '@driftt/dds.icons/light/faCirclePlay'
import { faComment } from '@driftt/dds.icons/light/faComment'
import { faEnvelope } from '@driftt/dds.icons/light/faEnvelope'
import { faEnvelopeOpen } from '@driftt/dds.icons/light/faEnvelopeOpen'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faInboxIn } from '@driftt/dds.icons/light/faInboxIn'
import { faInboxOut } from '@driftt/dds.icons/light/faInboxOut'
import { faReply } from '@driftt/dds.icons/light/faReply'
import { Div } from '@driftt/tide-core'

import { DateRange } from 'components/DateRangePicker'
import TooltipHoverPortal from 'components/TooltipHoverPortal'
import { CurrentUserContext } from 'context/currentUserContext'
import { EngagementByDay } from 'modules/accountActivity/domain'
import { pluralize } from 'utils/formatStrings'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const getDay = (timestamp: number) => {
  const dt = new Date(timestamp)
  return `${dt.getDate()}`
}

const getWeekday = (timestamp: number) => {
  const dt = new Date(timestamp)
  return `${dt.toLocaleDateString('en-US', { weekday: 'short' })}`
}

const isDateAfterToday = (timestamp: number) => {
  const today = new Date()
  today.setHours(23, 59, 59, 998)
  return new Date(timestamp) > today
}

const isDateMonday = (timestamp: number) => {
  return new Date(timestamp).getDay() === 1
}

const emailActivityCount = (engagementByType: any, us: boolean) => {
  if (us) {
    const outreachEmails = engagementByType['Outreach outbound email'] || 0
    const salesforceEmails = engagementByType['Salesforce outbound email'] || 0
    return outreachEmails + salesforceEmails
  } else {
    return engagementByType['Outreach inbound email'] || 0
  }
}

interface EngagementIconsProps {
  day: EngagementByDay
  us: boolean
}

const EngagementIcons: React.FC<EngagementIconsProps> = ({ day, us = true }) => {
  const emailCount = emailActivityCount(day.engagementByType, us)
  const siteVisitCount = day.engagementByType['Site visit']
  const driftChatCount = day.engagementByType['Drift chat']
  const driftVideoViewCount = day.engagementByType['Drift Video view']
  const meetingBookedCount = day.engagementByType['Meeting booked']
  const salesforceEmailCount = day.engagementByType['Email']
  const inboundEmailCount = day.engagementByType['Inbound Email']
  const outboundEmailCount = day.engagementByType['Outbound Email']
  const emailOpenedCount = day.engagementByType['Email Opened']
  const emailReplyCount = day.engagementByType['Email Reply']
  const emailClickedCount = day.engagementByType['Email Clicked']
  const videCallCount = day.engagementByType['Video Call']

  return (
    <Div className={styles.engagementIcons}>
      {!us && siteVisitCount > 0 && (
        <div>
          <Icon size="extra-small" css={{ backgroundColor: '#24b232' }} icon={faEye} />
        </div>
      )}
      {emailCount > 0 && (
        <div>
          <Icon size="extra-small" css={{ backgroundColor: '#b987d9' }} icon={faEnvelope} />
        </div>
      )}
      {salesforceEmailCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$indigo7'
            }}
            icon={faEnvelope}
          />
        </div>
      )}
      {inboundEmailCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$pink7'
            }}
            icon={faInboxIn}
          />
        </div>
      )}
      {outboundEmailCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$lime7'
            }}
            icon={faInboxOut}
          />
        </div>
      )}
      {emailOpenedCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$cyan7'
            }}
            icon={faEnvelopeOpen}
          />
        </div>
      )}
      {videCallCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$red7'
            }}
            icon={faCameraWeb}
          />
        </div>
      )}
      {emailReplyCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$purple7'
            }}
            icon={faReply}
          />
        </div>
      )}
      {emailClickedCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '$teal7'
            }}
            icon={faArrowPointer}
          />
        </div>
      )}
      {driftChatCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '#5196ed'
            }}
            icon={faComment}
          />
        </div>
      )}
      {!us && driftVideoViewCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '#d8863d'
            }}
            icon={faCirclePlay}
          />
        </div>
      )}
      {meetingBookedCount > 0 && (
        <div>
          <Icon
            size="extra-small"
            css={{
              backgroundColor: '#f05b4d'
            }}
            icon={faCalendarCheck}
          />
        </div>
      )}
    </Div>
  )
}

interface WeeklyHoverProps {
  day: EngagementByDay
  dayConatinerRef: any
}

const DailyRecap: React.FC<WeeklyHoverProps> = ({ day, dayConatinerRef }) => {
  const emailSentCount = emailActivityCount(day.engagementByType, true)
  const emailReceivedCount = emailActivityCount(day.engagementByType, false)
  const siteVisitCount = day.engagementByType['Site visit']
  const driftChatCount = day.engagementByType['Drift chat']
  const driftVideoViewCount = day.engagementByType['Drift Video view']
  const meetingBookedCount = day.engagementByType['Meeting booked']
  const salesforceEmailCount = day.engagementByType['Email']
  const inboundEmailCount = day.engagementByType['Inbound Email']
  const outboundEmailCount = day.engagementByType['Outbound Email']
  const emailOpenedCount = day.engagementByType['Email Opened']
  const emailReplyCount = day.engagementByType['Email Reply']
  const emailClickedCount = day.engagementByType['Email Clicked']
  const videCallCount = day.engagementByType['Video Call']

  const [positionStyles, setPositionStyles] = useState({})

  useEffect(() => {
    if (dayConatinerRef?.current) {
      const { bottom, right } = dayConatinerRef.current.getBoundingClientRect()
      setPositionStyles({ top: bottom - 80, left: right + 5, display: 'block' })
    }
  }, [dayConatinerRef])

  return (
    <Div style={positionStyles} className={styles.weeklyRecapContainer}>
      <Div className={styles.leftArrow} />
      <Div className={styles.weeklyRecap}>
        {meetingBookedCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              height="16px"
              width="16px"
              css={{
                backgroundColor: '#f05b4d'
              }}
              icon={faCalendarCheck}
            />
            <Div className={styles.recapTitle}>{`${meetingBookedCount} ${pluralize(
              'meeting',
              driftChatCount
            )} booked`}</Div>
          </Div>
        )}
        {driftVideoViewCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '#d8863d'
              }}
              icon={faCirclePlay}
            />
            <Div className={styles.recapTitle}>{`${driftVideoViewCount} Drift Video ${pluralize(
              'view',
              driftChatCount
            )}`}</Div>
          </Div>
        )}
        {driftChatCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '#5196ed'
              }}
              icon={faComment}
            />
            <Div className={styles.recapTitle}>{`${driftChatCount} Drift ${pluralize(
              'chat',
              driftChatCount
            )}`}</Div>
          </Div>
        )}

        {salesforceEmailCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$indigo7'
              }}
              icon={faEnvelope}
            />
            <Div className={styles.recapTitle}>{`${salesforceEmailCount} ${pluralize(
              'Email',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}

        {inboundEmailCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$pink7'
              }}
              icon={faInboxIn}
            />
            <Div className={styles.recapTitle}>{`${inboundEmailCount} Inbound ${pluralize(
              'email',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}

        {outboundEmailCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$lime7'
              }}
              icon={faInboxOut}
            />
            <Div className={styles.recapTitle}>{`${outboundEmailCount} Outbound ${pluralize(
              'email',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}

        {emailOpenedCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$cyan7'
              }}
              icon={faEnvelopeOpen}
            />
            <Div className={styles.recapTitle}>{`${emailOpenedCount} Email ${pluralize(
              'open',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}

        {emailReplyCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$purple7'
              }}
              icon={faReply}
            />
            <Div className={styles.recapTitle}>{`${emailReplyCount} Email ${pluralize(
              'reply',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}
        {emailClickedCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$teal7'
              }}
              icon={faArrowPointer}
            />
            <Div className={styles.recapTitle}>{`${emailClickedCount} Email ${pluralize(
              'click',
              emailClickedCount
            )}`}</Div>
          </Div>
        )}

        {videCallCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '$red7'
              }}
              icon={faCameraWeb}
            />
            <Div className={styles.recapTitle}>{`${videCallCount} Zoom ${pluralize(
              'call',
              salesforceEmailCount
            )}`}</Div>
          </Div>
        )}
        {(!!emailSentCount || !!emailReceivedCount) && (
          <Div className={styles.recapEmail}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '#b987d9'
              }}
              icon={faEnvelope}
            />
            <Div className={styles.recapEmailBody}>
              {!!emailSentCount && (
                <Div className={styles.recapTitle}>
                  {`${emailSentCount} email${emailSentCount > 1 ? 's' : ''} sent`}
                </Div>
              )}
              {!!emailReceivedCount && (
                <Div className={styles.recapTitle}>
                  {`${emailReceivedCount} email${emailReceivedCount > 1 ? 's' : ''} received`}
                </Div>
              )}
            </Div>
          </Div>
        )}
        {siteVisitCount > 0 && (
          <Div className={styles.recapRow}>
            <Icon
              size="extra-small"
              css={{
                backgroundColor: '#24b232'
              }}
              icon={faEye}
            />
            <Div className={styles.recapTitle}>{`${siteVisitCount} site visit${
              siteVisitCount > 1 ? 's' : ''
            }`}</Div>
          </Div>
        )}
      </Div>
    </Div>
  )
}

interface TimelineByDayProps {
  day: EngagementByDay
  key: any
  setActiveDayRef: (day: HTMLDivElement | null) => void
  dateRange?: DateRange
  setDateRange?: (newDate: DateRange | undefined) => void
}

const TimelineByDay: React.FC<TimelineByDayProps> = ({
  day,
  key,
  dateRange,
  setActiveDayRef,
  setDateRange
}) => {
  const currentUser = useContext(CurrentUserContext)

  const dayConatinerRef = useRef<HTMLDivElement>(null)
  const refElement = useRef<HTMLDivElement>(null)

  const isSelectedDay = dateRange?.startDate.isSame(moment(day.startOfDay), 'Day')

  const clickHandler = () => {
    if (isSelectedDay) {
      setDateRange && setDateRange(undefined)
    } else {
      currentUser?.orgId &&
        segmentTrack(TrackType.TIMELINE_DAY_CLICKED, { orgId: currentUser.orgId })
      const newDateRange = {
        startDate: moment(day.startOfDay).startOf('day'),
        endDate: moment(day.startOfDay).endOf('day')
      }
      setDateRange && setDateRange(newDateRange)
    }
  }

  useEffect(() => {
    if (moment(day.startOfDay).isSame(moment(), 'day') && !!dayConatinerRef?.current) {
      setActiveDayRef(dayConatinerRef.current)
    }
  }, [day.startOfDay, setActiveDayRef])

  return (
    <div
      className={cx(styles.sliceContainer, {
        [styles.disabledSliceContainer]: isDateAfterToday(day.startOfDay),
        [styles.emptySliceContainer]: isEmpty(day.engagementByType),
        [styles.selectedSliceContainer]: isSelectedDay
      })}
      onClick={clickHandler}
      key={key}
      ref={dayConatinerRef}>
      <TooltipHoverPortal
        className={styles.hoverTooltip}
        trackingEventName={TrackType.TIMELINE_DAY_HOVERED}
        boundaryRef={dayConatinerRef.current}
        refElement={refElement.current}
        tooltipBody={
          !isEmpty(day.engagementByType) ? (
            <DailyRecap day={day} dayConatinerRef={dayConatinerRef} />
          ) : (
            <></>
          )
        }>
        <Div className={styles.timelineSlice} ref={refElement}>
          <Div className={styles.usActivities}>
            <EngagementIcons day={day} us={true} />
          </Div>
          <Div className={styles.themActivities}>
            <EngagementIcons day={day} us={false} />
          </Div>
          <Div className={styles.timelineSliceDate}>
            <span>{getDay(day.startOfDay)}</span>
            <span
              className={cx(styles.timelineSliceWeekday, {
                [styles.weekdayIsMonday]: isDateMonday(day.startOfDay)
              })}>
              {getWeekday(day.startOfDay)}
            </span>
          </Div>
        </Div>
      </TooltipHoverPortal>
    </div>
  )
}

export default TimelineByDay
